/**
 * Saved-view manager governance and history HTML.
 */
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import type {
  LeadFactorySavedView,
  LeadFactorySavedViewAccess,
  LeadFactorySavedViewEvent
} from "./dashboard-lead-factory-types.js";

export function savedViewManagerLoadingHtml(): string {
  return `
    <div class="detail-inner saved-view-manager">
      <div class="detail-header">
        <span class="eyebrow">Saved View</span>
        <h2>Loading manager...</h2>
      </div>
    </div>
  `;
}

export function savedViewManagerHtml(
  view: LeadFactorySavedView,
  events: LeadFactorySavedViewEvent[],
  access: LeadFactorySavedViewAccess[] | undefined,
  canGovern: boolean
): string {
  return `
    <div class="detail-inner saved-view-manager" data-saved-view-manager="${escapeAttr(view.id)}">
      <div class="detail-header saved-view-manager-header">
        <div>
          <span class="eyebrow">Saved View</span>
          <h2>${escapeHtml(view.name)}</h2>
          <p class="subtle">${escapeHtml(labelize(view.scope))} · ${escapeHtml(labelize(view.visibility ?? "team"))}</p>
        </div>
        <button class="button ghost" type="button" data-saved-view-manager-close>Close</button>
      </div>
      <div class="saved-view-manager-actions">
        <button class="button secondary" type="button" data-saved-view-manager-copy>Copy link</button>
        ${
          canGovern ? "" : `<p class="subtle">Governance actions are available to the creator, managers, and admins.</p>`
        }
      </div>
      <div class="saved-view-manager-grid">
        <div>
          <span>Created by</span>
          <strong>${escapeHtml(view.created_by)}</strong>
        </div>
        <div>
          <span>Created</span>
          <strong>${escapeHtml(new Date(view.created_at).toLocaleString())}</strong>
        </div>
        <div>
          <span>Updated</span>
          <strong>${escapeHtml(new Date(view.updated_at).toLocaleString())}</strong>
        </div>
      </div>
      ${canGovern ? savedViewManagerGovernanceHtml(view, access ?? []) : ""}
      <section class="saved-view-manager-section">
        <h3>Filters</h3>
        <pre class="saved-view-filter-preview">${escapeHtml(JSON.stringify(view.filters, null, 2))}</pre>
      </section>
      <section class="saved-view-manager-section">
        <h3>Access</h3>
        ${
          canGovern
            ? access && access.length > 0
              ? `<p class="saved-view-access">${access.map(savedViewAccessHtml).join("")}</p>`
              : `<p class="empty">No extra operators have access to this saved view.</p>`
            : `<p class="empty">Access list is visible only to saved-view governors.</p>`
        }
      </section>
      <section class="saved-view-manager-section">
        <h3>History</h3>
        ${savedViewHistoryHtml(view, events, [])}
      </section>
    </div>
  `;
}

export function savedViewManagerGovernanceHtml(view: LeadFactorySavedView, access: LeadFactorySavedViewAccess[]): string {
  return `
    <section class="saved-view-manager-section saved-view-manager-forms">
      <h3>Governance</h3>
      <form class="saved-view-manager-form" data-saved-view-manager-rename-form>
        <input type="text" maxlength="80" value="${escapeAttr(view.name)}" aria-label="Saved view name" data-saved-view-manager-name>
        <button class="button secondary" type="submit">Rename</button>
      </form>
      <form class="saved-view-manager-form" data-saved-view-manager-grant-form>
        <input type="text" maxlength="120" placeholder="operator@example.com" aria-label="Grant operator" data-saved-view-manager-grant-actor>
        <select aria-label="Access role" data-saved-view-manager-grant-role>
          <option value="viewer">Viewer</option>
          <option value="manager">Manager</option>
        </select>
        <input type="text" maxlength="40" placeholder="2999-01-01T00:00:00.000Z" aria-label="Access expiration" data-saved-view-manager-grant-expires>
        <button class="button secondary" type="submit">Grant</button>
      </form>
      <form class="saved-view-manager-form" data-saved-view-manager-revoke-form>
        <select aria-label="Revoke operator" data-saved-view-manager-revoke-actor ${access.length === 0 ? "disabled" : ""}>
          ${access.length > 0 ? access.map(savedViewAccessOptionHtml).join("") : `<option value="">No access rows</option>`}
        </select>
        <button class="button secondary" type="submit" ${access.length === 0 ? "disabled" : ""}>Revoke</button>
      </form>
      <form class="saved-view-manager-form saved-view-manager-delete-form" data-saved-view-manager-delete-form>
        <label>
          <input type="checkbox" data-saved-view-manager-delete-confirm>
          <span>Confirm delete</span>
        </label>
        <button class="button ghost danger" type="submit">Delete</button>
      </form>
    </section>
  `;
}

export function savedViewAccessOptionHtml(access: LeadFactorySavedViewAccess): string {
  return `<option value="${escapeAttr(access.actor)}">${escapeHtml(access.actor)} · ${escapeHtml(labelize(access.access_role))}</option>`;
}

export function savedViewHistoryHtml(view: LeadFactorySavedView, events: LeadFactorySavedViewEvent[], access: LeadFactorySavedViewAccess[] = []): string {
  return `
    <div class="saved-view-history" data-saved-view-history>
      <div class="saved-view-history-heading">
        <strong>${escapeHtml(view.name)}</strong>
        <span>${escapeHtml(labelize(view.scope))} · ${escapeHtml(view.visibility ?? "team")}</span>
      </div>
      ${access.length > 0 ? `<p class="saved-view-access">${access.map(savedViewAccessHtml).join("")}</p>` : ""}
      ${
        events.length > 0
          ? `<ol>
              ${events.slice(0, 8).map(savedViewHistoryEventHtml).join("")}
            </ol>`
          : `<p class="empty">No saved-view history has been recorded yet.</p>`
      }
    </div>
  `;
}

export function savedViewAccessHtml(access: LeadFactorySavedViewAccess): string {
  const expiresAt = access.expires_at ? ` · expires ${new Date(access.expires_at).toLocaleString()}` : "";
  return `<span>${escapeHtml(access.actor)} · ${escapeHtml(labelize(access.access_role))}${escapeHtml(expiresAt)}</span>`;
}

export function savedViewHistoryEventHtml(event: LeadFactorySavedViewEvent): string {
  return `
    <li>
      <span>${escapeHtml(labelize(event.event_type))}</span>
      <small>${escapeHtml(event.actor)} · ${escapeHtml(new Date(event.created_at).toLocaleString())}</small>
      ${savedViewHistoryEventDetail(event)}
    </li>
  `;
}

export function savedViewHistoryEventDetail(event: LeadFactorySavedViewEvent): string {
  const previousName = typeof event.metadata.previousName === "string" ? event.metadata.previousName : "";
  const name = typeof event.metadata.name === "string" ? event.metadata.name : "";
  if (previousName && name && previousName !== name) {
    return `<em>${escapeHtml(previousName)} -> ${escapeHtml(name)}</em>`;
  }
  if (name) return `<em>${escapeHtml(name)}</em>`;
  if (event.note) return `<em>${escapeHtml(event.note)}</em>`;
  return "";
}

